import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react"
import styled from "styled-components"
import AnnouncementBanner, { BannerLevel } from "./AnnouncementBanner"

export interface BannerConfig {
  id: string
  message: string
  level?: BannerLevel
  dismissible?: boolean
  actionText?: string
  onActionClick?: () => void
  autoHide?: boolean
  autoHideDelay?: number
  persistent?: boolean // Won't be stored in localStorage if true
}

interface BannerContextType {
  banners: BannerConfig[]
  showBanner: (config: BannerConfig) => void
  hideBanner: (id: string) => void
  clearAllBanners: () => void
  bannerHeight: number
}

const BannerContext = createContext<BannerContextType | undefined>(undefined)

export const useBannerContext = () => {
  const context = useContext(BannerContext)
  if (!context) {
    throw new Error("useBannerContext must be used within a BannerProvider")
  }
  return context
}

const BannersContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  display: flex;
  flex-direction: column;
`

export const BannerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [banners, setBanners] = useState<BannerConfig[]>([])
  const [bannerHeight, setBannerHeight] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  const showBanner = useCallback((config: BannerConfig) => {
    // Check if this banner was previously dismissed (unless it's persistent)
    if (!config.persistent && config.id) {
      const wasDismissed = localStorage.getItem(`banner-dismissed-${config.id}`)
      if (wasDismissed === "true") {
        return // Don't show the banner if it was previously dismissed
      }
    }

    setBanners(prev => {
      // Remove existing banner with same ID if it exists
      const filtered = prev.filter(banner => banner.id !== config.id)
      return [...filtered, config]
    })

    // Auto-hide if specified
    if (config.autoHide && config.autoHideDelay) {
      setTimeout(() => {
        hideBanner(config.id)
      }, config.autoHideDelay)
    }
  }, [])

  const hideBanner = useCallback((id: string) => {
    setBanners(prev => prev.filter(banner => banner.id !== id))
  }, [])

  const clearAllBanners = useCallback(() => {
    setBanners([])
  }, [])

  // Measure actual banner container height
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const height = entry.target.getBoundingClientRect().height
        setBannerHeight(height)
      }
    })

    resizeObserver.observe(container)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  const handleBannerDismiss = (id: string, persistent?: boolean) => {
    hideBanner(id)

    // Store dismissal in localStorage unless it's persistent
    if (!persistent) {
      localStorage.setItem(`banner-dismissed-${id}`, "true")
    }
  }

  return (
    <BannerContext.Provider
      value={{ banners, showBanner, hideBanner, clearAllBanners, bannerHeight }}
    >
      <BannersContainer ref={containerRef} data-banner-container="true">
        {banners.map(banner => (
          <AnnouncementBanner
            key={banner.id}
            id={banner.persistent ? undefined : banner.id}
            message={banner.message}
            level={banner.level}
            dismissible={banner.dismissible}
            actionText={banner.actionText}
            onActionClick={banner.onActionClick}
            onDismiss={() => handleBannerDismiss(banner.id, banner.persistent)}
          />
        ))}
      </BannersContainer>
      {children}
    </BannerContext.Provider>
  )
}
